/** @jsxImportSource @emotion/react */
import { useEffect, useState } from "react";
import { PageWrapper } from "../../components/page-skeleton/PageWrapper";
import { CardContainer } from "../../components/page-skeleton/CardContainer";
import { PageHeader } from "../../components/page-skeleton/PageHeader";
import { useNavigate, useParams } from "react-router-dom";
import { SpinnerBlock } from "../common/Spinner";
import { css } from "@emotion/react";
import { routes } from "../../appRoutingHelpers";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { AddMenuProductsSelector } from "./components/AddMenuProductsSelector";
import { StickyBottomBar } from "../../components/StickyBottomBar";
import { stickyBottomBarBodyStyle } from "../shop/app-home/generic-styles";
import { useForm, FormProvider, useFieldArray } from "react-hook-form";
import { MenuProduct } from "./models/Menus";
import { CardBody } from "../../components/page-skeleton/CardBody";
import { ResetDraftSelector } from "./components/ResetDraftSelector";
import { StyledForm } from "../../components/StyledForm";
import { toastr } from "react-redux-toastr";
import { ActionSpinner } from "../../components/ActionSpinner";
import { PublishAndPushMenuSelector } from "./components/PublishAndPushMenuSelector";
import { ButtonPrimary } from "../../components/NewButtons";
import { message } from "antd";
import { MenuCategoryGrid } from "./components/MenuCategoryGrid";
import { useSelector } from "react-redux";
import * as authSelectors from "../auth/authSelectors";
import {
  useGetMenu,
  useGetMenuAvailableProducts,
  useGetMenuDraftProducts,
  useGetMenuProducts,
  usePublishMenuDraft,
  usePushMenuToSalesChannels,
  useUpdateMenuDraftProducts,
} from "./service/menus-api";
import { getErrorMessage, useHandleFetchError } from "../../error-handler";
import { useSearchParamsManipulation } from "../../hooks/use-searchparams-manipulation";

export type MenuProductsFormValues = {
  products: MenuProduct[];
};

const additionalContainerStyle = css`
  padding-bottom: 24px;
`;

export const MenuEdit = () => {
  const { id } = useParams<{
    id: string;
  }>();
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );
  const { tags } = useSearchParamsManipulation();
  const [
    savePublishAndPushIsLoading,
    setSavePublishAndPushIsLoading,
  ] = useState<boolean>(false);

  const navigate = useNavigate();
  const methods = useForm<MenuProductsFormValues>({
    defaultValues: {
      products: [],
    },
  });
  const [messageApi, contextHolder] = message.useMessage();
  const { append, fields, remove } = useFieldArray<MenuProductsFormValues>({
    control: methods.control,
    name: "products",
  });

  const {
    data: products,
    isLoading: productsAreLoading,
    refetch: refetchProducts,
    error: getProductsError,
  } = useGetMenuProducts(organizationKeyName, id);

  const {
    data: availableProducts,
    isLoading: availableProductsAreLoading,
  } = useGetMenuAvailableProducts(organizationKeyName, true, id, {
    tags: tags?.split(",") || [],
  });

  // Will get cached Menu from MenuUpserLayout
  // So no additional API call is made
  const {
    data,
    isLoading,
    refetch: refetchMenu,
    error: getMenuError,
  } = useGetMenu(organizationKeyName, id);

  const {
    data: draftProducts,
    isLoading: draftProductsAreLoading,
    refetch: refetchDraftProducts,
    error: getDraftProductsError,
  } = useGetMenuDraftProducts(organizationKeyName, id);

  const {
    mutateAsync: updateMenuDraftProducts,
    isLoading: updatingMenuDraftProductsIsLoading,
  } = useUpdateMenuDraftProducts(id);
  const { mutateAsync: publishMenuDraft } = usePublishMenuDraft();
  const { mutateAsync: pushMenuToSalesChannels } = usePushMenuToSalesChannels(
    id
  );

  const {
    watch,
    reset,
    formState: { isDirty },
  } = methods;

  const menuProducts = watch("products");
  const changesMade = isDirty;

  const saveMenuAsDraft = async (values: MenuProductsFormValues) => {
    if (!id) return;
    try {
      await updateMenuDraftProducts({
        data: {
          products: values.products.map((p) => ({
            id: p.id,
            sortKey: p.sortKey,
            appearance: p.appearance,
          })),
        },
        id,
        organizationKeyName,
      });
      // We only want to refetch when API calls succeeds if saving as draft
      // Therefor refetching manually and not in updateMenuDraftProducts query
      refetchDraftProducts();
      refetchProducts();
      refetchMenu();
      navigate(routes.menuList(organizationKeyName));
      toastr.success("Success", "Menu changes saved succesfully");
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error);
      toastr.error("Error", errorMessage);
    }
  };

  const savePublishAndPush = async (values: MenuProductsFormValues) => {
    if (!id) return;
    try {
      setSavePublishAndPushIsLoading(true);
      // Setting menu as draft
      await updateMenuDraftProducts({
        data: {
          products: values.products.map((p) => ({
            id: p.id,
            sortKey: p.sortKey,
            appearance: p.appearance,
          })),
        },
        id,
        organizationKeyName,
      });
      // Publishing menu (From draft -> Active)
      await publishMenuDraft({
        id,
        organizationKeyName,
      });
      // When saving, publishing and pushing we refetch after publish ends and not right after updateMenuDraftProducts call succeeds.
      refetchDraftProducts();
      refetchProducts();
      refetchMenu();
      // Pushing externally right away
      await pushMenuToSalesChannels({
        menuId: id,
        organizationKeyName,
      });
      navigate(routes.menuList(organizationKeyName));
      toastr.success("Success", "Menu changes pushed succesfully");
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error);
      toastr.error("Error", errorMessage);
    } finally {
      setSavePublishAndPushIsLoading(false);
    }
  };

  useEffect(() => {
    if (!data || !draftProducts || !products) {
      return;
    }

    if (data.haveChanges) {
      reset({
        products: draftProducts,
      });
    } else {
      reset({
        products: products,
      });
    }
  }, [products, draftProducts, data, reset]);

  useEffect(() => {
    if (isLoading || productsAreLoading || draftProductsAreLoading || !data) {
      return;
    }

    messageApi.info({
      content: "Drag and drop to sort menu products in a category",
      duration: 8,
    });

    return () => {
      messageApi.destroy();
    };
  }, [
    isLoading,
    productsAreLoading,
    draftProductsAreLoading,
    data,
    messageApi,
  ]);

  useHandleFetchError(getMenuError);
  useHandleFetchError(getProductsError);
  useHandleFetchError(getDraftProductsError);

  if (isLoading || productsAreLoading || draftProductsAreLoading || !data) {
    return <SpinnerBlock />;
  }

  return (
    <>
      {contextHolder}
      <PageWrapper>
        <FormProvider {...methods}>
          <StyledForm>
            <CardContainer>
              <PageHeader
                title="Menu"
                subtitle="Add and remove products to this menu"
              >
                {data.haveChanges && (
                  <ResetDraftSelector id={id} buttonType="regular" />
                )}
                {menuProducts.length > 0 && (
                  <AddMenuProductsSelector
                    append={append}
                    availableProducts={availableProducts}
                    availableProductsAreLoading={availableProductsAreLoading}
                    buttonType="regular"
                  />
                )}
              </PageHeader>
              <CardBody additionalStyles={additionalContainerStyle}>
                {menuProducts.length === 0 ? (
                  <AddMenuProductsSelector
                    append={append}
                    availableProducts={availableProducts}
                    availableProductsAreLoading={availableProductsAreLoading}
                    buttonType="placeholder"
                  />
                ) : (
                  <MenuCategoryGrid
                    remove={remove}
                    fields={fields}
                    originalMenuProducts={products}
                    availableProducts={availableProducts}
                    availableProductsAreLoading={availableProductsAreLoading}
                    append={append}
                  />
                )}
              </CardBody>
            </CardContainer>
          </StyledForm>
        </FormProvider>
      </PageWrapper>
      {(data.haveChanges || changesMade) && (
        <StickyBottomBar>
          <div css={stickyBottomBarBodyStyle}>
            <ButtonPrimary
              onClick={() => {
                if (data.haveChanges) {
                  reset({
                    products: draftProducts,
                  });
                } else {
                  reset({
                    products: products,
                  });
                }
              }}
              type="button"
            >
              Reset changes
            </ButtonPrimary>
            <ButtonPrimary
              onClick={methods.handleSubmit(saveMenuAsDraft)}
              type="button"
              disabled={
                updatingMenuDraftProductsIsLoading &&
                !savePublishAndPushIsLoading
              }
              icon={<FontAwesomeIcon icon={solid("save")} />}
            >
              {updatingMenuDraftProductsIsLoading &&
              !savePublishAndPushIsLoading ? (
                <ActionSpinner />
              ) : (
                "Save changes"
              )}
            </ButtonPrimary>
            <PublishAndPushMenuSelector
              isMenuPage={true}
              menuId={id}
              savePublishAndPushPublishIsLoading={savePublishAndPushIsLoading}
              savePublishAndPush={methods.handleSubmit(savePublishAndPush)}
            />
          </div>
        </StickyBottomBar>
      )}
    </>
  );
};
